import { STAGE_ORDER, type Stage } from '@crm/shared';
import {
  DndContext,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useMemo, useState } from 'react';

import type { Opportunity } from '../../lib/api';

import { KanbanColumn } from './KanbanColumn';
import { OpportunityCardPresentation } from './OpportunityCard';

const STAGE_LABELS: Record<Stage, string> = {
  identified: 'Identified',
  qualifying: 'Qualifying',
  compliance_review: 'Compliance review',
  proposal: 'Proposal',
  onboarding: 'Onboarding',
  active: 'Active',
};

type ConfirmRequest = {
  opportunity: Opportunity;
  fromStage: Stage;
  toStage: Stage;
  stepsJumped: number;
};

type Props = {
  opportunities: Opportunity[];
  onStageChange: (opportunityId: string, toStage: Stage) => Promise<void> | void;
  loading?: boolean;
};

export function KanbanBoard({ opportunities, onStageChange, loading }: Props) {
  const [pending, setPending] = useState<ConfirmRequest | null>(null);
  const [announce, setAnnounce] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeStage, setActiveStage] = useState<Stage | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 100, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const grouped = useMemo(() => {
    const byStage: Record<Stage, Opportunity[]> = {
      identified: [],
      qualifying: [],
      compliance_review: [],
      proposal: [],
      onboarding: [],
      active: [],
    };
    for (const o of opportunities) byStage[o.stage].push(o);
    return byStage;
  }, [opportunities]);

  const byId = useMemo(() => {
    const map = new Map<string, Opportunity>();
    for (const o of opportunities) map.set(o.id, o);
    return map;
  }, [opportunities]);

  const activeOpportunity = activeId ? (byId.get(activeId) ?? null) : null;

  function resolveStage(overId: string): Stage | null {
    if ((STAGE_ORDER as readonly string[]).includes(overId)) return overId as Stage;
    const opp = byId.get(overId);
    return opp?.stage ?? null;
  }

  // Hybrid collision detection: prefer pointer-within (precise drop on
  // column whitespace), fall back to rect-intersection (catches when
  // pointer is over a card so we still resolve to its column).
  function collisionDetection(...args: Parameters<typeof rectIntersection>) {
    const pointerCollisions = pointerWithin(...args);
    if (pointerCollisions.length > 0) return pointerCollisions;
    return rectIntersection(...args);
  }

  function handleDragStart(e: DragStartEvent) {
    const data = e.active.data.current as { opportunity?: Opportunity } | undefined;
    const opp = data?.opportunity;
    if (opp) {
      setActiveId(opp.id);
      setActiveStage(opp.stage);
      setAnnounce(`Drag started for ${opp.name}`);
    }
  }

  async function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    setActiveId(null);
    setActiveStage(null);
    if (!over) return;

    const opportunity = (active.data.current as { opportunity?: Opportunity } | undefined)
      ?.opportunity;
    if (!opportunity) return;

    const toStage = resolveStage(String(over.id));
    if (!toStage) return;
    if (opportunity.stage === toStage) return;

    const fromIndex = STAGE_ORDER.indexOf(opportunity.stage);
    const toIndex = STAGE_ORDER.indexOf(toStage);
    const steps = Math.abs(toIndex - fromIndex);

    if (steps > 1) {
      setPending({ opportunity, fromStage: opportunity.stage, toStage, stepsJumped: steps });
      return;
    }

    setAnnounce(`Moved ${opportunity.name} to ${STAGE_LABELS[toStage]}`);
    await onStageChange(opportunity.id, toStage);
  }

  async function confirmJump() {
    if (!pending) return;
    setAnnounce(`Moved ${pending.opportunity.name} to ${STAGE_LABELS[pending.toStage]}`);
    await onStageChange(pending.opportunity.id, pending.toStage);
    setPending(null);
  }

  if (loading) {
    return (
      <div className="grid grid-cols-6 gap-0 h-full">
        {STAGE_ORDER.map((s) => (
          <div key={s} className="px-5 py-4 border-r border-platinum">
            <div className="h-5 bg-platinum rounded animate-pulse" />
            <div className="h-3 mt-2 w-24 bg-platinum rounded animate-pulse" />
            <div className="mt-4 space-y-3">
              <div className="h-16 bg-platinum/60 rounded animate-pulse" />
              <div className="h-16 bg-platinum/60 rounded animate-pulse" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        data-testid="kanban-live-region"
      >
        {announce}
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => {
          setActiveId(null);
          setActiveStage(null);
        }}
      >
        <div
          className="flex h-full overflow-x-auto bg-glacier border-t border-platinum"
          data-testid="kanban-board"
        >
          {STAGE_ORDER.map((stage, idx) => (
            <KanbanColumn
              key={stage}
              stage={stage}
              index={idx + 1}
              label={STAGE_LABELS[stage]}
              opportunities={grouped[stage]}
              sourceStage={activeStage}
            />
          ))}
        </div>

        <DragOverlay dropAnimation={null}>
          {activeOpportunity ? (
            <div className="rotate-1">
              <OpportunityCardPresentation opportunity={activeOpportunity} dragging />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {pending && (
        <ConfirmJumpPanel
          request={pending}
          onCancel={() => setPending(null)}
          onConfirm={confirmJump}
          labels={STAGE_LABELS}
        />
      )}
    </>
  );
}

function ConfirmJumpPanel({
  request,
  onCancel,
  onConfirm,
  labels,
}: {
  request: ConfirmRequest;
  onCancel: () => void;
  onConfirm: () => void;
  labels: Record<Stage, string>;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-jump-title"
      className="fixed inset-0 z-50 flex"
    >
      <div className="flex-1 bg-graphite/20" onClick={onCancel} />
      <aside className="w-[420px] max-w-full bg-surface shadow-panel h-full p-8 flex flex-col">
        <p className="label-uppercase">— Stage jump</p>
        <h2
          id="confirm-jump-title"
          className="mt-3 font-display text-2xl font-semibold text-graphite leading-snug"
        >
          Saut de {request.stepsJumped} stage{request.stepsJumped > 1 ? 's' : ''}
        </h2>
        <p className="mt-5 text-sm text-ink-secondary leading-relaxed">
          Moving <span className="text-ink">{request.opportunity.name}</span> from{' '}
          <span className="text-ink">{labels[request.fromStage]}</span> to{' '}
          <span className="text-ink">{labels[request.toStage]}</span> skips{' '}
          {request.stepsJumped - 1} {request.stepsJumped - 1 > 1 ? 'stages' : 'stage'}. This will
          recompute the score and create an audit entry.
        </p>
        <div className="mt-auto flex gap-3 pt-8">
          <button type="button" className="btn-secondary flex-1" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn-primary flex-1" onClick={onConfirm}>
            Confirm
          </button>
        </div>
      </aside>
    </div>
  );
}
